// src/components/sections/Resources.jsx
import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { useTheme } from '../../hooks/useTheme';
import { 
  FileText, 
  Code, 
  ArrowRight
} from 'lucide-react';
import styles from './Resources.styles';

const Resources = () => {
  const { isDarkMode } = useTheme();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3 
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.8, ease: [0.04, 0.62, 0.23, 0.98] }
    }
  };

  const resources = [
    {
      icon: <FileText size={24} />,
      title: "API Reference",
      description: "Comprehensive guides, API reference, and examples to help you integrate quickly.",
      link: "https://docs.daftara.dev/",
      linkText: "View Documentation",
      isClickable: true
    },
    {
      icon: <Code size={24} />,
      title: "SDKs & CLI",
      description: "Official client libraries and command-line tools for accelerated development.",
      link: "#",
      linkText: "Coming Soon",
      isClickable: false
    }
  ];

  return (
    <section 
      ref={ref}
      style={styles.section}
      id="resources"
    >
      <div style={styles.container}>
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.div 
            variants={itemVariants}
            style={styles.header}
          >
            <h2 style={styles.heading(isDarkMode)}>
              Developer <span style={styles.headingHighlight}>Resources</span>
            </h2>
            <p style={styles.subheading(isDarkMode)}>
              Everything you need to integrate with our API successfully.
              From comprehensive documentation to ready-to-use code samples.
            </p>
          </motion.div>

          <motion.div 
            variants={itemVariants}
            style={styles.resourcesGrid}
          >
            {resources.map((resource, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                style={styles.resourceCard(isDarkMode, resource.isClickable)}
                whileHover={resource.isClickable ? styles.cardHover(isDarkMode) : styles.nonClickableCardHover(isDarkMode)}
              >
                <div style={styles.iconContainer(isDarkMode, resource.isClickable)}>
                  {resource.icon}
                </div>
                <h3 style={styles.resourceTitle(isDarkMode, resource.isClickable)}>
                  {resource.title}
                </h3>
                <p style={styles.resourceDescription(isDarkMode, resource.isClickable)}>
                  {resource.description}
                </p>
                {resource.isClickable ? (
                  <a 
                    href={resource.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={styles.resourceLink}
                    onMouseOver={(e) => {
                      e.target.style.textDecoration = 'underline';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.textDecoration = 'none';
                    }}
                  >
                    {resource.linkText}
                    <ArrowRight size={16} />
                  </a>
                ) : (
                  <span style={styles.comingSoonLink}>
                    {resource.linkText}
                    {/* <ArrowRight size={16} /> */}
                  </span>
                )}
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Resources;